import { skipToken } from '@reduxjs/toolkit/query';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import {
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordHeader,
    RecordMoreMenu,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import TabsGallery, { TabType } from 'components/TabsGallery';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useGetExpenseByIdQuery } from 'store/api/expenses';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useConfirmDeleteExpense } from './useConfirmDeleteExpense';
import GeneralView from './views/GeneralView';

const USED_CLASSIFIERS = ['EXPENSE_TYPE', 'EXPENSE_STATUS_TYPE'];
const UPDATE_PERMISSIONS = ['UPDATE.EXPENSES.ALL', 'UPDATE.EXPENSES.MATTER_ACCESS', 'UPDATE.EXPENSES.MY_OWN'];
const DELETE_PERMISSIONS = ['DELETE.EXPENSES.ALL', 'DELETE.EXPENSES.MATTER_ACCESS', 'DELETE.EXPENSES.MY_OWN'];

const ExpensePage = (): JSX.Element => {
    const { id: expenseId } = useParams();
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/expenses');
    const isExpenseEntryDisabled = useExpenseEntryDisabled();
    const isNew = useIsNewDesign();
    const canUpdate = useHasPermissions(UPDATE_PERMISSIONS);
    const canDelete = useHasPermissions(DELETE_PERMISSIONS);
    const confirmDelete = useConfirmDeleteExpense();

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const { data: expense, isLoading: isLoadingExpense } = useGetExpenseByIdQuery(expenseId ?? skipToken, {
        refetchOnMountOrArgChange: true,
    });
    const isDraft = !!expense?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/expenses');
    const draftActions = useDraftActions({
        draft: expense?.isDraft ? { type: 'expense', id: expense.id } : null,
        createRoute: '/expense/new',
        onDeleted: leaveDeleted,
    });
    const { draft } = draftActions;
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    const tabs = useMemo<TabType[]>(() => {
        if (!expense) return [];
        return [
            {
                id: 'general',
                label: t('matters.general'),
                component: <GeneralView expense={expense} classifiers={classifiers} isNew={isNew} />,
            },
        ];
    }, [expense, classifiers, isNew]);

    if (isLoadingExpense || isLoadingClassifiers) {
        return <Loader />;
    }

    if (!expense) {
        return <></>;
    }

    const typeLabel = expense.expenseTypeCode
        ? getClassifierText(classifiers['EXPENSE_TYPE'], expense.expenseTypeCode)
        : '';
    const title = expense.description?.trim() || typeLabel || t('expenses.expense');
    const isEditable = expense.isEditable && !isExpenseEntryDisabled;

    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;

    // The General view reads the draft from here: what it misses, and whom it is shared with.
    const tabsGallery = (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <TabsGallery
                tabs={tabs}
                tabGroupId="expense-page"
                trackTabsInHistoryState
                usePathBasedRouting
                wrapperClassName={RECORD_TABS_CLASS}
                panelClassName={isNew ? RECORD_PANEL_CLASS : 'min-h-0 flex-1 overflow-y-auto'}
                // The new design's white band runs from the header through the tab strip, edge to edge.
                className={isNew ? RECORD_TAB_STRIP_CLASS : undefined}
            />
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    );

    if (isNew) {
        // Edit went to the sections; Delete, which only the edit form had, moves to the ⋯ menu.
        return (
            <RecordPage
                header={
                    isDraft ? (
                        <div className="flex flex-col gap-4">
                            <RecordHeader
                                title={title}
                                chips={<DraftChip />}
                                meta={draft ? <DraftMeta draft={draft} /> : undefined}
                                actions={<DraftRecordActions actions={draftActions} />}
                            />
                            {draftNotice}
                        </div>
                    ) : (
                        <RecordHeader
                            title={title}
                            pin={{ kind: 'expense', id: expense.id }}
                            actions={
                                <RecordMoreMenu
                                    menuItems={
                                        canDelete
                                            ? [
                                                  {
                                                      title: t('expenses.delete'),
                                                      variant: 'destructive',
                                                      disabled: !expense.isDeletable || isExpenseEntryDisabled,
                                                      handler: () => confirmDelete(expense.id),
                                                  },
                                              ]
                                            : []
                                    }
                                />
                            }
                        />
                    )
                }
            >
                <SectionEditProvider canEdit={isEditable && canUpdate}>{tabsGallery}</SectionEditProvider>
            </RecordPage>
        );
    }

    return (
        <PaddedLayout className="flex h-full flex-col overflow-hidden">
            <PageHeader
                title={title}
                showBreadcrumb
                onBack={goBack}
                buttons={
                    isDraft ? (
                        <div className="flex items-center justify-end gap-2">
                            <DraftRecordActions actions={draftActions} />
                        </div>
                    ) : isEditable ? (
                        <PermissionButton
                            permissions={UPDATE_PERMISSIONS}
                            variant="outlined"
                            size="small"
                            onClick={() => navigateWithReturn(`/expense/${expenseId}/edit`)}
                        >
                            <FormattedMessage id="edit" />
                        </PermissionButton>
                    ) : undefined
                }
            >
                {draft ? (
                    <div className="flex w-full flex-wrap items-center gap-4 py-4 max-sm:gap-2">
                        <DraftChip />
                        <DraftMeta draft={draft} />
                    </div>
                ) : null}
            </PageHeader>
            {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
            {tabsGallery}
        </PaddedLayout>
    );
};

export default ExpensePage;
